/**
 * SQL source pane (S6) — connection + database pickers + a Monaco mini-editor
 * with the connection's language services (same `SqlMonaco` the Query Editor
 * embeds), and the Preview trigger. Tiles are cross-connection: every
 * connected connection is offered regardless of engine. The database picker
 * reuses the shared `DatabaseSelector` (QueryStatusBar/NotebookToolbar) — the
 * form owns the value; without it a connection saved with no default database
 * dead-ended the Visual tab on "No tables in this database yet".
 *
 * S8: mounted for EVERY source tab. The connection + Preview row is shared
 * chrome; the editor stays mounted but CSS-hidden off the SQL tab so the
 * Visual tab's edit pipeline can commit through `editorRef` (SqlMonaco is
 * uncontrolled after mount — a live handle is the only reliable write path)
 * and so the buffer/undo history survives tab switches.
 */
import { Play } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { RandomDotmSpinner } from '@/components/ui/RandomDotmSpinner';
import type { DatabaseEngineId } from '@/core/engines';
import { SqlMonaco } from '../../../common/components/SqlMonaco';
import type { SqlMonacoHandle } from '../../../common/utils/sqlMonacoTypes';
import { ConnectionSelector } from '../../../common/components/ConnectionSelector';
import { DatabaseSelector } from '../../../common/components/DatabaseSelector';
import { useConnectionStore } from '../../../../connections/store/connectionStore';
import { safeConnectionAlias, safeConnectionServer } from '@/types/safeConnectionDisplay';

const KNOWN_ENGINES: ReadonlySet<string> = new Set([
  'mssql',
  'pgsql',
  'mysql',
  'oracle',
  'sqlite',
  'kusto',
]);

export function SqlSourcePane({
  connectionId,
  onConnectionChange,
  database,
  onDatabaseChange,
  sql,
  onSqlChange,
  onPreview,
  previewRunning,
  editorRef,
  editorHidden = false,
}: {
  connectionId: string;
  onConnectionChange: (connectionId: string) => void;
  /** Form-owned database the tile query runs against (undefined = none). */
  database?: string;
  onDatabaseChange: (database: string) => void;
  sql: string;
  onSqlChange: (sql: string) => void;
  onPreview: () => void;
  previewRunning: boolean;
  /** Live handle for programmatic writes (VQE commits, template/AI insertion). */
  editorRef?: React.Ref<SqlMonacoHandle>;
  /** S8: keep the editor mounted but hidden while another source tab is active. */
  editorHidden?: boolean;
}) {
  const active = useConnectionStore((s) => s.activeConnections).find((c) => c.id === connectionId);
  const saved = useConnectionStore((s) => s.connections).find((c) => c.id === connectionId);
  // The saved connection keeps the engine while the connection is closed, so
  // the editor's language service never flips to the 'mssql' fallback.
  const engine = active?.connection.engine ?? saved?.engine;
  const engineId: DatabaseEngineId = KNOWN_ENGINES.has(engine ?? '')
    ? (engine as DatabaseEngineId)
    : 'mssql';
  /** WHY the Preview button is disabled (null = runnable). */
  const previewBlocker = !connectionId
    ? 'Pick a connection first'
    : !sql.trim()
      ? 'Write a query first'
      : null;

  return (
    <div className="flex min-h-0 flex-col gap-2">
      <div className="flex items-center gap-2">
        <div className="min-w-0 flex-1" data-testid="add-tile-connection-select">
          <ConnectionSelector
            connectionId={connectionId || undefined}
            connectionName={active?.connection.alias ? safeConnectionAlias(active.connection) : undefined}
            serverHost={safeConnectionServer(active?.connection)}
            onConnectionChange={(id) => onConnectionChange(id)}
            showAllEngines
          />
        </div>
        {/* Compact database pick beside the connection — the recovery path for
            connections saved without a default database (the empty-tables hint
            on the Visual tab points here). */}
        {connectionId ? (
          <div
            className="flex max-w-36 shrink-0 items-center"
            data-testid="add-tile-database-select"
          >
            <DatabaseSelector
              connectionId={connectionId}
              database={database}
              onDatabaseChange={onDatabaseChange}
              className="text-xs"
            />
          </div>
        ) : null}
        {/* Default (primary) variant, NOT `secondary`: this theme resolves
            `--secondary` to the exact same token as `--muted`, so a secondary
            Preview button was pixel-identical to the disabled treatment
            (`disabled:bg-muted`) and read as dead in both themes.
            WHY tooltip on the span wrapper: a disabled Button is
            `pointer-events-none`, so a `title` on the button itself never
            fires while disabled (same idiom as the dialog's submit button). */}
        <span title={previewBlocker ?? undefined} className="shrink-0">
          <Button
            type="button"
            size="sm"
            className="h-7 gap-1.5 disabled:bg-muted disabled:text-muted-foreground"
            onClick={onPreview}
            disabled={previewRunning || Boolean(previewBlocker)}
            title={previewBlocker ? undefined : 'Run the query read-only and suggest a tile'}
            data-testid="add-tile-preview-button"
          >
            {previewRunning ? <RandomDotmSpinner size={14} /> : <Play className="h-3.5 w-3.5" />}
            Preview
          </Button>
        </span>
      </div>
      <div
        className={
          editorHidden
            ? 'hidden'
            : // The editor sits INSIDE the dialog surface, so it must not paint
              // Monaco's own warm-grey (`vs-dark` #1E1E1E) against the cool
              // navy dialog background — the seam read as a foreign panel.
              // Monaco's theme CSS is injected unlayered (it beats Tailwind's
              // `@layer utilities` on cascade layers alone), hence the explicit
              // `!important` on the two theme variables it derives from.
              'relative h-40 shrink-0 overflow-hidden rounded-md border border-border ' +
              '[&_.monaco-editor]:[--vscode-editor-background:var(--background)_!important] ' +
              '[&_.monaco-editor]:[--vscode-editorGutter-background:var(--background)_!important]'
        }
        data-testid="add-tile-sql-pane"
      >
        <SqlMonaco
          ref={editorRef}
          value={sql}
          onChange={onSqlChange}
          engineId={engineId}
          connectionId={connectionId || undefined}
          database={database}
          height="100%"
        />
        {/* Ghost hint over the empty buffer — Monaco has no placeholder of its
            own. `pointer-events-none` so the click still lands in the editor;
            offsets match Monaco's line-number gutter + 8px top padding. There
            is deliberately NO keyboard shortcut in the copy: the dialog binds
            none, and Preview is a button-only action. */}
        {!sql.trim() && (
          <span
            aria-hidden
            className="pointer-events-none absolute left-[68px] top-2 select-none font-mono text-[13px] text-muted-foreground/60"
            data-testid="add-tile-sql-placeholder"
          >
            SELECT … — then hit Preview to see the tile
          </span>
        )}
      </div>
    </div>
  );
}
